import { Component, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { AdditionalDetail } from '../shared/model/ui/additional-detail';
import { MhsControl } from '../shared/model/ui/mhs-control';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { MhsService } from '../shared/service/mhs.service';
import { CmHelp, ContentType } from '../shared/model/cm/managed-content.model';
import { ModalService } from '../shared/service/modal.service';
import { RepeatableQuestions } from '../shared/model/ui/repeatable-question';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { GenericFormComponent } from '../generic-form/generic-form.component';

@Component({
  selector: 'app-additional-details',
  standalone: true,
  imports: [ReactiveFormsModule, GenericFormComponent],
  templateUrl: './additional-details.component.html',
  styleUrls: ['./additional-details.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AdditionalDetailsComponent implements OnInit {

  @Input() parentForm: UntypedFormGroup;
  @Input() mhsControl: MhsControl;

  public repeatableDetails: AdditionalDetail[] = [];
  private index: number;

  public allRepeatableQuestions: RepeatableQuestions[] = [];
  public parentKey: string;
  public currentParentKey: string;

  public questionType: string;
  public confirmDeleteModalContent: CmHelp;
  public deleteModalContentHeading: string;
  public deleteModalContentBody: string;

  public confirmDiagnosisModalContent: CmHelp;
  public diagnosisModalContentHeading: string;
  public diagnosisModalContentBody: string;

  private indexToRemove: number;

  public addButtonProps = {
    buttonType: "button",
    size: "default",
    text: "Add Another",
    variant: "text",
    keyId: "primary",
    type: "button",
    icon: "plus"
  }

  public removeButtonProps = {
    buttonType: "button",
    size: "default",
    text: "Delete",
    variant: "text",
    keyId: "remove",
    type: "button",
    icon: "trash-alt"
  }
  fb: any;

  constructor(public mhsService: MhsService,
              private cmService: ManagedContentService,
              private modalService: ModalService) { }

  ngOnInit(): void {

    this.parentKey = this.mhsControl.key;

    if(this.mhsService){
      this.mhsService.updateMhsParentKey(this.parentKey);
      this.mhsService.mhsParentKey.subscribe(key =>{
        if(key){
          this.currentParentKey = key;
        }
      });
      this.mhsService.mhsIndexToRemove.subscribe(index =>{
        this.indexToRemove = index;
      });
    }

    this.index = 1;
    let additionalDetailKey: string;
   
    if(this.mhsControl.additionalDetails != null && this.mhsControl.additionalDetails.length > 0){
      // In a couple of cases, the additionalDetail.key is numbered differently than its parent in the legacy form, 
      // so grabbing the key off the first additional detail rather than the parent to accommodate those cases.
      // Using mhsControl.key if additionalDetails are null
      additionalDetailKey = this.mhsControl.additionalDetails[0].key.split("-")[0] + "-" + this.mhsControl.additionalDetails[0].key.split("-")[1];
    } else {
      // Instantiating the additionDetails array for questions with no additionalDetails except for physician
      this.mhsControl.additionalDetails = [];
      additionalDetailKey = this.mhsControl.key
    }
    
    // If details are repeatable, make a copy of additionalDetails before modifying them
    if(this.mhsControl.additionalDetailsAreRepeatable){
      for(let additionalDetail of this.mhsControl.additionalDetails){ 
        let newAdditionalDetail = new AdditionalDetail();
        Object.assign(newAdditionalDetail, additionalDetail);
        this.repeatableDetails.push(newAdditionalDetail);

         // Use the label of the first detail in the buttons
        if(additionalDetail.sortOrderNumber === 1 && additionalDetail.label != null){
          this.questionType = additionalDetail.label;
          this.addButtonProps.text = this.addButtonProps.text + " " + this.questionType;
          this.removeButtonProps.text = this.removeButtonProps.text + " this " + this.questionType;
        }
      }

      // Add index to additionalDetails property names
      this.mhsControl.additionalDetails =  this.indexDetailKeys(this.mhsControl.additionalDetails, this.index);
      this.setupRepeatableDetails(this.mhsControl.additionalDetails);
    }

    this.confirmDeleteModalContent = this.cmService.getContentById(ContentType.HELP, "delete-description");

    this.deleteModalContentHeading = this.confirmDeleteModalContent.title;
    this.deleteModalContentBody = this.confirmDeleteModalContent.body;

    this.confirmDiagnosisModalContent = this.cmService.getContentById(ContentType.HELP, "delete-diagnosis");

    this.diagnosisModalContentHeading = this.confirmDiagnosisModalContent.title;
    this.diagnosisModalContentBody = this.confirmDiagnosisModalContent.body;

  }

  setupRepeatableDetails(additionalDetails: AdditionalDetail[]){
    let repeatableQuestions: RepeatableQuestions = new RepeatableQuestions();
    if(this.mhsService.allRepeatableQuestions.length === 0){
      let detailId = this.getDetailId(additionalDetails);
      let allRepeatableQuestions: RepeatableQuestions[] = [];
      repeatableQuestions.questionNumber = detailId;
      let lrepeats: AdditionalDetail[][] = [];
      lrepeats.push(this.mhsControl.additionalDetails);
      repeatableQuestions.repeats = lrepeats;
      allRepeatableQuestions.push(repeatableQuestions);
      this.mhsService.allRepeatableQuestions = allRepeatableQuestions;
      this.allRepeatableQuestions = allRepeatableQuestions;
    } else {

      let index: number = 0;
      let match: number = 0;
      for(let repeatableQuestions of this.mhsService.allRepeatableQuestions){
        if(repeatableQuestions.questionNumber === this.currentParentKey){
          match++;          
        }
        index++;
      }
      
      // If the question is not already in allRepeatableQuestions, add it
      if(match === 0){
        let detailId = this.getDetailId(additionalDetails);
        let allRepeatableQuestions: RepeatableQuestions[] = [];
        allRepeatableQuestions = this.mhsService.allRepeatableQuestions;
        repeatableQuestions.questionNumber = detailId;
        repeatableQuestions.repeats = [];
        repeatableQuestions.repeats.push(this.mhsControl.additionalDetails);
        allRepeatableQuestions.push(repeatableQuestions);
        this.mhsService.allRepeatableQuestions = allRepeatableQuestions;
      } 
    }
  }

  addRepeatableDetails(additionalDetails: AdditionalDetail[], detailId: string){
    let allRepeatableQuestions: RepeatableQuestions[] = [];
    allRepeatableQuestions = this.mhsService.allRepeatableQuestions;

    let index: number = 0;
    for(let repeatableQuestions of this.mhsService.allRepeatableQuestions){
      if(repeatableQuestions.questionNumber === detailId){
        let repeatableQuestions: RepeatableQuestions = new RepeatableQuestions();
        repeatableQuestions.questionNumber = detailId;
        repeatableQuestions.repeats = [];
        repeatableQuestions.repeats = this.mhsService.allRepeatableQuestions[index].repeats;
        repeatableQuestions.repeats.push(additionalDetails);
        this.mhsService.allRepeatableQuestions[index].repeats = repeatableQuestions.repeats;
      } 
      index++;
    }
  }

  getDetailId(additionalDetails: AdditionalDetail[]){
    let detail: AdditionalDetail = additionalDetails[0];
    let splitKey: string[];
    let detailId: string;
    let detailDescription: string;

    if(detail.key.indexOf("_") > -1){
      splitKey = detail.key.split("_");
      detailId = splitKey[0];
      detailDescription = splitKey[1].substring(2);

    } else {
      splitKey = detail.key.split("-");
      detailId = splitKey.slice(0,2).join("-");
      detailDescription = splitKey.slice(2).join("-");
    }

    return detailId;
  }

  yesDelete(component: string){
    // Remove the details from the array
    let index: number = 0;
    for(let repeatableQuestions of this.mhsService.allRepeatableQuestions){
      if(repeatableQuestions.questionNumber === this.currentParentKey){
        for(let repeat of repeatableQuestions.repeats){
          if(index === this.indexToRemove){
            let removedDetails = repeatableQuestions.repeats.splice(index, 1);

            // this.mhsService.updateDeleteControls(true);

            for(let removedDetailGroup of removedDetails){
              for(let removedDetail of removedDetailGroup){
                if(this.parentForm){
                  if(this.parentForm.contains(removedDetail.key)){
                    this.parentForm.controls[removedDetail.key].clearValidators();
                    this.parentForm.removeControl(removedDetail.key);
                  }
                }
              }
            }
          }
          index++;
        }
      }
    }   
    
    if(component === "delete-modal"){
      this.modalService.closeModal('prism-ui-modal-delete-modal');
    }

    if(component === "diagnosis-modal"){
      this.modalService.closeModal('prism-ui-modal-diagnosis-modal');
    }
    
  }

  onCancelModal(component: string){
    if(component === "delete-modal"){
      this.modalService.closeModal('prism-ui-modal-delete-modal');
    }

    if(component === "diagnosis-modal"){
      this.modalService.closeModal('prism-ui-modal-diagnosis-modal');
    }
  }

  indexDetailKeys(additionalDetails: AdditionalDetail[], index: number): AdditionalDetail[] {
    for(let additionalDetail of additionalDetails){
      this.indexDetailKey(additionalDetail, index);
    }
    return additionalDetails;
  }

  indexDetailKey(additionalDetail: AdditionalDetail, index: number): AdditionalDetail{
    let splitKey: string[];
      let detailId: string;
      let detailDescription: string;

      if(additionalDetail.key.indexOf("_") > -1){
        splitKey = additionalDetail.key.split("_");
        detailId = splitKey[0];
        detailDescription = splitKey[1].substring(2);

      } else {
        splitKey = additionalDetail.key.split("-");
        detailId = splitKey.slice(0,2).join("-");
        detailDescription = splitKey.slice(2).join("-");
      }
      additionalDetail.key = detailId + "_" + index + "-" + detailDescription;
      
      return additionalDetail;
  }

  public addDetails(){
    this.index = this.getMaxIndex();
    let newAdditionalDetails: AdditionalDetail[] = [];
    for(let repeatableDetail of this.repeatableDetails){
      let newAdditionalDetail = new AdditionalDetail();
      Object.assign(newAdditionalDetail, repeatableDetail);
      newAdditionalDetails.push(newAdditionalDetail);
    }
    newAdditionalDetails = this.indexDetailKeys(newAdditionalDetails, this.index);
    let detailId = this.getDetailId(newAdditionalDetails);
    this.addRepeatableDetails(newAdditionalDetails, detailId);
  }

  public getMaxIndex(){
    let maxIndex: number = 0;
    let splitKey: string[];
    let currentIndex: string;

    for(let rq of this.mhsService.allRepeatableQuestions){
      if(rq.questionNumber === this.currentParentKey){
        for(let r of rq.repeats){
          splitKey = r[0].key.split("_");
          currentIndex = Array.from(splitKey[1])[0];
          if(Number(currentIndex + 1) > maxIndex){
            maxIndex = Number(currentIndex) + 1;
          }
        }
      }
    }
    
    return maxIndex;
  }

  public removeDetails(indexToRemove: number, questionType: string){
    
    this.mhsService.updateMhsParentKey(this.mhsControl.key);
    if(this.mhsService){
      this.mhsService.updateMhsIndexToRemove(indexToRemove);
    }

    if(questionType.toLowerCase() == "diagnosis" || questionType.toLowerCase() == "diagnosis/description"){
      this.modalService.showModal('prism-ui-modal-diagnosis-modal');
    } else if(questionType.toLowerCase() == "description" ){
      this.modalService.showModal('prism-ui-modal-delete-modal');
    }
  }
}